/**
 * Workflow Legal ID Verification — main component.
 * Mirrors LegalIdVerification.tsx architecture: separate hooks for state + Veriff events.
 *
 * States:
 * - initializing: Waiting for IDME_INIT postMessage from parent
 * - ready: Intro screen shown, waiting for user to click "Open camera"
 * - capturing: Veriff SDK is active, user scanning ID
 * - processing: Veriff finished, submitting + polling + fetching
 * - confirming: Showing extracted ID data for review
 * - done: User confirmed, postMessage sent to parent
 * - error: Something failed
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useConsumer } from "../ConsumerProvider";
import { submitWorkflowInput, getWorkflowExecution, fetchLegalIdDetails } from "../../shared/api/workflowEngine";
import type { WorkflowExecutionResponse } from "../../shared/types/workflowEngine";
import { useWorkflowVeriffEvents } from "./useWorkflowVeriffEvents";
import { WorkflowIntroScreen } from "./WorkflowIntroScreen";
import { WorkflowProcessingScreen } from "./WorkflowProcessingScreen";
import { WorkflowConfirmationScreen } from "./WorkflowConfirmationScreen";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { logger } from "../../shared/utils/logger";
import { LanguageSelector } from "../faceVerification/LanguageSelector";
import { isVeriffMobile } from "../../shared/utils/veriffEnvironment";
import styles from "../legalIdVerification/LegalIdVerification.module.scss";

export type WorkflowLegalIdState = "initializing" | "ready" | "capturing" | "processing" | "confirming" | "done" | "error";

interface InitPayload {
  veriffUrl: string;
  executionId: string;
  workflowId: number;
  inspectionName: string;
  authToken?: string;
  apiBaseUrl?: string;
}

const DEFAULT_API_BASE = "https://enroll.idme.test/api/workflow";

// Thrown when a poll is abandoned because the component unmounted. Callers
// distinguish it from real failures so they skip setState/error reporting on a
// dead component (no React "set state on unmounted" warnings, no wasted calls).
class PollCancelledError extends Error {
  constructor() {
    super("Polling cancelled");
    this.name = "PollCancelledError";
  }
}

// Poll the execution until `predicate` holds or it reaches a terminal state (returned as-is
// so callers can surface the failure). Throws on timeout, or PollCancelledError if
// `isCancelled` flips true (checked before each fetch and before each sleep).
async function pollExecution(
  apiBase: string,
  data: InitPayload,
  predicate: (exec: WorkflowExecutionResponse) => boolean,
  opts: { timeoutMs?: number; isCancelled?: () => boolean } = {},
): Promise<WorkflowExecutionResponse> {
  const { timeoutMs = 120_000, isCancelled } = opts;
  const intervalMs = 2000;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (isCancelled?.()) throw new PollCancelledError();
    const current = await getWorkflowExecution(apiBase, data.workflowId, data.executionId, data.authToken);
    if (isCancelled?.()) throw new PollCancelledError();
    if (current.status === "FAILED" || current.status === "CANCELLED") return current;
    if (predicate(current)) return current;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error("Polling timed out");
}

export function WorkflowLegalIdVerification() {
  const { t } = useTranslation();
  const { fragnoLogoEnabled } = useConsumer();
  const [state, setState] = useState<WorkflowLegalIdState>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [initData, setInitData] = useState<InitPayload | null>(null);
  const [attributes, setAttributes] = useState<Record<string, unknown>>({});
  const [showSpinner, setShowSpinner] = useState(true);
  const initDataRef = useRef<InitPayload | null>(null);
  const parentOriginRef = useRef<string>("*");
  // The Veriff session id the active confirm decision is for. The activity ignores a decision
  // that doesn't name the current session, so confirm/retake echo this back.
  const decisionInspectionIdRef = useRef<string | null>(null);
  // Flipped on unmount so in-flight polls bail out instead of setState-ing a dead component.
  const mountedRef = useRef(true);
  // Guards the confirm/retake decision submit so a rapid double-click can't fire two
  // concurrent submissions before the buttons re-render away.
  const submittingRef = useRef(false);

  useEffect(() => () => { mountedRef.current = false; }, []);

  // Derive and validate parent origin from document.referrer on mount
  useEffect(() => {
    if (!document.referrer) return;
    try {
      const origin = new URL(document.referrer).origin;
      const hostname = new URL(document.referrer).hostname;
      const isAllowed = getAllowedDomains().some(
        domain => hostname === domain || hostname.endsWith(`.${domain}`)
      );
      if (isAllowed) {
        parentOriginRef.current = origin;
        logger.log("[WorkflowLegalId] Parent origin validated:", origin);
      } else {
        logger.warn("[WorkflowLegalId] Parent origin not in allowlist:", origin);
      }
    } catch {
      logger.warn("[WorkflowLegalId] Could not parse document.referrer:", document.referrer);
    }
  }, []);

  useEffect(() => { initDataRef.current = initData; }, [initData]);

  // ── Error helper ──────────────────────────────────────────────────────
  const handleVerificationError = useCallback((errorCode: string, message: string) => {
    setError(message);
    setState("error");
    logger.error(`[WorkflowLegalId] Error [${errorCode}]:`, message);
    window.parent.postMessage({
      source: "idme-verification",
      type: "VERIFICATION_ERROR",
      payload: { errorCode, message, executionId: initDataRef.current?.executionId },
    }, parentOriginRef.current);
  }, []);

  // ── Post-Veriff flow: submit → poll → fetch ───────────────────────────
  const handleVeriffFinished = useCallback(async () => {
    const data = initDataRef.current;
    if (!data) return;

    const apiBase = data.apiBaseUrl || DEFAULT_API_BASE;

    try {
      // Submit _input
      logger.log("[WorkflowLegalId] Submitting _input to WE");
      await submitWorkflowInput(
        apiBase,
        data.workflowId,
        data.executionId,
        {
          inspectionName: data.inspectionName,
          input: {
            inputType: "sdk_document_capture",
            user_completed_sdk: true,
            capture_method: "sdk",
          },
        },
        data.authToken
      );

      // The activity holds the inspection open at the confirm gate (NEEDS_INPUT
      // legal_id_decision) once the Veriff webhook lands. Wait for that directive.
      logger.log("[WorkflowLegalId] Waiting for the confirm directive");
      const exec = await pollExecution(apiBase, data, (c) => c.ux?.inputType === "legal_id_decision",
        { isCancelled: () => !mountedRef.current });

      if (exec.status === "FAILED" || exec.status === "CANCELLED") {
        const msg = (exec.result?.data as Record<string, unknown>)?.message as string ?? "Verification failed";
        handleVerificationError("poll_failed", msg);
        return;
      }

      // The decision must name the session it's for — the activity ignores stale ones.
      const uxData = (exec.ux?.data as Record<string, unknown>) ?? {};
      decisionInspectionIdRef.current = (uxData.inspectionId as string) ?? null;

      // Prefer rich attributes from the legal-id resource link; fall back to ux.data.
      const selfLink =
        (uxData.link as string | undefined) ??
        exec.inspections?.[data.inspectionName]?.result?.evidence?.link ??
        null;
      if (selfLink && selfLink.startsWith("/v1/verifications/legal-ids")) {
        const legalIdBase = apiBase.replace(/\/api\/workflow\b/, "/api/legalid");
        try {
          const details = await fetchLegalIdDetails(legalIdBase, selfLink, data.authToken);
          const attrs = (details.results as Record<string, unknown>)?.attributes as Record<string, unknown> | undefined;
          setAttributes(attrs ?? uxData);
        } catch (err) {
          logger.warn("[WorkflowLegalId] Could not fetch details, using ux.data:", err);
          setAttributes(uxData);
        }
      } else {
        setAttributes(uxData);
      }

      setState("confirming");
    } catch (err) {
      if (err instanceof PollCancelledError || !mountedRef.current) return;
      logger.error("[WorkflowLegalId] Post-Veriff flow failed:", err);
      handleVerificationError("submit_failed", err instanceof Error ? err.message : "Verification processing failed");
    }
  }, [handleVerificationError]);

  // ── Step 1: Listen for IDME_INIT from parent ──────────────────────────
  useEffect(() => {
    window.parent.postMessage(
      { source: "idme-verification", type: "IDME_READY" },
      parentOriginRef.current
    );

    const handler = (event: MessageEvent) => {
      if (!event.data || event.data.type !== "IDME_INIT") return;
      try {
        const senderHostname = new URL(event.origin).hostname;
        const allowed = getAllowedDomains().some(
          (d) => senderHostname === d || senderHostname.endsWith(`.${d}`)
        );
        if (!allowed) {
          logger.warn("[WorkflowLegalId] IDME_INIT rejected from origin:", event.origin);
          return;
        }
      } catch {
        logger.warn("[WorkflowLegalId] Could not parse IDME_INIT origin:", event.origin);
        return;
      }
      const payload = event.data.payload as InitPayload;
      logger.log("[WorkflowLegalId] Received init:", payload.executionId);
      setInitData(payload);
      initDataRef.current = payload;
      setState("ready");
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // ── Step 2: Veriff events (mirrors useVeriffEvents from IVA flow) ─────
  useWorkflowVeriffEvents({
    veriffSessionUrl: initData?.veriffUrl ?? null,
    state,
    setState,
    onVerificationFinished: handleVeriffFinished,
    onVerificationError: handleVerificationError,
  });

  // ── Step 3: User clicks "Open camera" ─────────────────────────────────
  const handleOpenCamera = useCallback(() => {
    logger.log("[WorkflowLegalId] User clicked Open camera — starting Veriff");
    setState("capturing");
  }, []);

  // ── Step 3b: User indicates they don't have a valid ID ────────────────
  const handleNoId = useCallback(() => {
    logger.log("[WorkflowLegalId] User indicated no valid ID — notifying parent");
    handleVerificationError("user_no_id", "User indicated they do not have a valid government-issued photo ID");
  }, [handleVerificationError]);

  // ── Step 4: Toggle spinner ────────────────────────────────────────────
  const handleHideAnimation = useCallback(() => {
    setShowSpinner(prev => !prev);
  }, []);

  // ── Step 5: User confirms → submit the decision, then notify the parent ─
  const handleConfirm = useCallback(async () => {
    // Block a second submit from a rapid double-click before the buttons re-render away.
    if (submittingRef.current) return;
    const data = initDataRef.current;
    if (!data) return;
    submittingRef.current = true;
    setState("processing");
    const apiBase = data.apiBaseUrl || DEFAULT_API_BASE;
    try {
      logger.log("[WorkflowLegalId] User confirmed — submitting legal_id_decision=confirm");
      await submitWorkflowInput(apiBase, data.workflowId, data.executionId, {
        inspectionName: data.inspectionName,
        input: {
          inputType: "legal_id_decision",
          legalIdDecision: "confirm",
          decisionForInspectionId: decisionInspectionIdRef.current,
        },
      }, data.authToken);

      // Confirm completes the inspection; WE advances (to face for multi-step, else COMPLETED).
      const exec = await pollExecution(apiBase, data, (c) =>
        c.status === "COMPLETED" || (!!c.currentInspection && c.currentInspection !== data.inspectionName),
        { isCancelled: () => !mountedRef.current });
      if (exec.status === "FAILED" || exec.status === "CANCELLED") {
        submittingRef.current = false;
        const msg = (exec.result?.data as Record<string, unknown>)?.message as string ?? "Verification failed";
        handleVerificationError("poll_failed", msg);
        return;
      }
      if (!mountedRef.current) return;

      window.parent.postMessage({
        source: "idme-verification",
        type: "VERIFICATION_COMPLETE",
        payload: {
          executionId: data.executionId,
          workflowId: data.workflowId,
          status: "confirmed",
          attributes,
          multiStep: exec.status !== "COMPLETED",
        },
      }, parentOriginRef.current);
      setState("done");
    } catch (err) {
      if (err instanceof PollCancelledError || !mountedRef.current) return;
      submittingRef.current = false;
      logger.error("[WorkflowLegalId] Confirm flow failed:", err);
      handleVerificationError("submit_failed", err instanceof Error ? err.message : "Confirmation failed");
    }
  }, [handleVerificationError, attributes]);

  // ── Step 6: User requests retake → recapture against a fresh session ────
  // Submit the decision; the activity holds the inspection open and mints a fresh Veriff
  // session, re-issuing the capture directive. Pick up the new session URL and re-mount
  // capture — no host error, no forward-route.
  const handleRetake = useCallback(async () => {
    // Same double-submit guard as confirm — a double-click must not fire two retakes.
    if (submittingRef.current) return;
    const data = initDataRef.current;
    if (!data) return;
    submittingRef.current = true;
    setError(null);
    setState("processing");
    const apiBase = data.apiBaseUrl || DEFAULT_API_BASE;
    try {
      logger.log("[WorkflowLegalId] User requested retake — submitting legal_id_decision=retake");
      await submitWorkflowInput(apiBase, data.workflowId, data.executionId, {
        inspectionName: data.inspectionName,
        input: {
          inputType: "legal_id_decision",
          legalIdDecision: "retake",
          decisionForInspectionId: decisionInspectionIdRef.current,
        },
      }, data.authToken);

      // Wait for the fresh capture directive, then re-mount Veriff on the new session URL.
      const exec = await pollExecution(apiBase, data, (c) => c.ux?.inputType === "sdk_document_capture",
        { isCancelled: () => !mountedRef.current });
      if (exec.status === "FAILED" || exec.status === "CANCELLED") {
        submittingRef.current = false;
        const msg = (exec.result?.data as Record<string, unknown>)?.message as string ?? "Verification failed";
        handleVerificationError("poll_failed", msg);
        return;
      }
      if (!mountedRef.current) return;
      // The capture directive only ever carries `link` (LegalIdVerifyActivity sets it when
      // non-null) — never url/sessionUrl/veriffUrl, so there are no other shapes to fall back
      // to. We fail rather than reuse the prior veriffUrl: that's the now-dead session.
      const uxData = (exec.ux?.data as Record<string, unknown>) ?? {};
      const newVeriffUrl = uxData.link as string | undefined;
      if (!newVeriffUrl) {
        logger.error("[WorkflowLegalId] Retake: no `link` in capture directive ux.data; keys:", Object.keys(uxData));
        submittingRef.current = false;
        handleVerificationError("submit_failed", "No Veriff session URL returned on retake");
        return;
      }
      const next = { ...data, veriffUrl: newVeriffUrl };
      initDataRef.current = next;
      setInitData(next);
      // New capture round — clear the guard so the next confirm/retake can submit.
      submittingRef.current = false;
      setState("capturing");
    } catch (err) {
      if (err instanceof PollCancelledError || !mountedRef.current) return;
      submittingRef.current = false;
      logger.error("[WorkflowLegalId] Retake flow failed:", err);
      handleVerificationError("submit_failed", err instanceof Error ? err.message : "Retake failed");
    }
  }, [handleVerificationError]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>

        {/* VEX-7983: with the Fragno logo on, BrandLayout renders the shared header instead */}
        {!fragnoLogoEnabled && (
          <header className={styles.logoContainer}>
            <div className={styles.logo}>
              <img src="/assets/svg/idme-logo.svg" alt="IDMe" />
            </div>
          </header>
        )}

        <main className={styles.contentContainer}>

          {/* Initializing — spinner while waiting for postMessage */}
          {state === "initializing" && (
            <div className={styles.card}>
              <div className={styles.spinner} />
            </div>
          )}

          {/* Ready — intro screen: "Take a photo of your ID" */}
          {state === "ready" && (
            <WorkflowIntroScreen onOpenCamera={handleOpenCamera} onNoId={handleNoId} />
          )}

          {/* Capturing — on desktop Veriff mounts into #veriff-root full-screen;
              on mobile Veriff renders its own modal and ignores the container. */}
          {state === "capturing" && !isVeriffMobile() && (
            <div id="veriff-root" className={styles.veriffContainer} />
          )}

          {/* Processing — spinner with hide/show toggle (matches IVA design) */}
          {state === "processing" && (
            <WorkflowProcessingScreen
              showSpinner={showSpinner}
              onHideAnimation={handleHideAnimation}
            />
          )}

          {/* Confirming — show extracted ID details for user to review */}
          {state === "confirming" && (
            <WorkflowConfirmationScreen
              attributes={attributes}
              onConfirm={handleConfirm}
              onRetake={handleRetake}
            />
          )}

          {/* Done */}
          {state === "done" && (
            <div className={styles.card}>
              <div className={styles.successIcon}>✓</div>
              <h2 className={styles.title}>{t('legalId.completed.title')}</h2>
              <p className={styles.subtitle}>{t('legalId.completed.subtitle')}</p>
            </div>
          )}

          {/* Error */}
          {state === "error" && (
            <div className={styles.card}>
              <div className={styles.errorIcon}>✕</div>
              <h2 className={styles.title}>{t('legalId.error.title')}</h2>
              <p className={styles.error}>{error}</p>
            </div>
          )}

        </main>

        <footer className={styles.footer}>
          <LanguageSelector />
          <div className={styles.footerLinks}>
            <a href="https://www.id.me/about" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('legalId.footer.whatIsIdme')}</a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a href="https://www.id.me/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('legalId.footer.termsOfService')}</a>
            <span aria-hidden="true" className={styles.footerDivider}>|</span>
            <a href="https://www.id.me/privacy" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>{t('legalId.footer.privacyPolicy')}</a>
          </div>
        </footer>

      </div>
    </div>
  );
}
